import { Configuration, SkusApi } from '@/api';
import ComboBox, { Item, MultiSelectProps, SingleSelectProps } from '@/components/select/ComboBox';
import { OtherSelectBoxProps } from '@/components/select/SelectBox';
import { Sku } from '@/models/Sku';
import { useAuth } from '@/modules/auth/contexts/AuthContext';

export interface ProductVariantItem {
  id: string;
  name: string;
  sku: Sku;
}

type ProductVariantComboBoxProps<T extends Item> =
  | (SingleSelectProps<T> &
      OtherSelectBoxProps<T> & {
        multiple?: false;
        supplierId?: string;
        hideLabel?: true;
      })
  | (MultiSelectProps<T> &
      OtherSelectBoxProps<T> & {
        multiple: true;
        supplierId?: string;
        hideLabel?: true;
      });

const ProductVariantComboBox = (props: ProductVariantComboBoxProps<ProductVariantItem>) => {
  const {
    multiple,
    label = multiple ? 'Products:' : 'Product:',
    placeholder = multiple ? 'Select products' : 'Select product',
    supplierId,
  } = props;

  const skusApi = new SkusApi(new Configuration(useAuth));

  const findSkus = async (query: string = ''): Promise<ProductVariantItem[]> => {
    const response = await skusApi.findSkus(query, { supplierId }, 0, 10);
    return response.items.map((sku) => ({
      id: sku.id,
      name: sku.name,
      sku: sku,
    }));
  };

  return (
    <ComboBox<ProductVariantItem>
      {...props}
      name="sku"
      label={label}
      hideLabel={props.hideLabel}
      placeholder={placeholder}
      queryFn={findSkus}
      queryKey={['sku', supplierId || '']}
    />
  );
};

export default ProductVariantComboBox;
